import { Link, useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageToggle } from "@/components/language-toggle";
import { useLanguage } from "@/components/language-provider";
import { Mascot } from "@/components/assistant/mascot";
import "@/components/assistant/assistant.css";
import { useSettings } from "@/components/settings-provider";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel,
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarHeader,
  SidebarFooter,
  useSidebar
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Send, Settings, LogOut, LayoutDashboard, HelpCircle, ChevronDown, Plus, User, Users, History, Bell, Eye, Pen, XCircle, CheckCheck, Clock, FileStack, ShieldCheck, Target, BookOpen, Menu, PanelLeft } from "lucide-react";
import { getApiUrl } from "@/lib/config";
import { planLabel, resolvePlan } from "@/lib/plan";
import { useCrmMenuFlag } from "@/lib/crm/api";
import { BillingNotice } from "@/components/billing-notice";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

interface Company {
  id: string;
  name: string;
  logoUrl?: string;
  plan: string;
}

interface AppLayoutProps {
  children: React.ReactNode;
  user: any;
  onLogout: () => void;
  plan?: string;
  currentPage?: string;
}

function CompanySwitcher({ user }: { user: any }) {
  const { t } = useLanguage();
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  
  useEffect(() => {
    const stored = localStorage.getItem("currentCompanyId");
    if (stored) setCurrentCompanyId(stored);
  }, []);

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies/list"],
    enabled: !!user && user.plan !== "free",
  });

  const { data: currentCompany } = useQuery<Company | null>({
    queryKey: ["/api/companies/my"],
    enabled: !!user,
  });

  const handleCompanySwitch = (companyId: string) => {
    setCurrentCompanyId(companyId);
    localStorage.setItem("currentCompanyId", companyId);
    window.location.reload();
  };

  const displayCompany = currentCompany || { name: t("company.personal"), plan: user?.plan || "free" };

  // A member of a company always sees the company (and its plan), whatever the cached user.plan says.
  if (!user || (!currentCompany && (user.plan || "free") === "free")) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-md bg-sidebar-accent/50">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs bg-primary/10">P</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{t("company.personal")}</p>
          <p className="text-xs text-muted-foreground">{planLabel("free", t)}</p>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-full justify-start gap-2 h-auto py-2 px-2" data-testid="button-company-switcher">
          <Avatar className="h-8 w-8">
            {currentCompany?.logoUrl ? (
              <AvatarImage src={currentCompany.logoUrl} alt={currentCompany.name} />
            ) : null}
            <AvatarFallback className="text-xs bg-primary/10">
              {displayCompany.name?.[0]?.toUpperCase() || "C"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium truncate">{displayCompany.name}</p>
            <p className="text-xs text-muted-foreground">{planLabel(displayCompany.plan, t)}</p>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t("company.switch")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {currentCompany && (
          <DropdownMenuItem
            onClick={() => handleCompanySwitch(currentCompany.id)}
            className={currentCompanyId === currentCompany.id ? "bg-accent" : ""}
            data-testid={`company-item-${currentCompany.id}`}
          >
            <Avatar className="h-6 w-6 mr-2">
              {currentCompany.logoUrl ? (
                <AvatarImage src={currentCompany.logoUrl} alt={currentCompany.name} />
              ) : null}
              <AvatarFallback className="text-xs">
                {currentCompany.name?.[0]?.toUpperCase() || "C"}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{currentCompany.name}</span>
          </DropdownMenuItem>
        )}
        {companies?.filter(c => c.id !== currentCompany?.id).map(company => (
          <DropdownMenuItem
            key={company.id}
            onClick={() => handleCompanySwitch(company.id)}
            data-testid={`company-item-${company.id}`}
          >
            <Avatar className="h-6 w-6 mr-2">
              {company.logoUrl ? (
                <AvatarImage src={company.logoUrl} alt={company.name} />
              ) : null}
              <AvatarFallback className="text-xs">
                {company.name?.[0]?.toUpperCase() || "C"}
              </AvatarFallback>
            </Avatar>
            <span className="truncate">{company.name}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/settings?action=create-company" className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            <span>{t("company.create")}</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


function NotificationBell() {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["/api/notifications/recent"],
    queryFn: async () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const res = await fetch(getApiUrl("/api/notifications/recent?count=5"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return { data: { notifications: [], unreadCount: 0 } };
      return res.json();
    },
    refetchInterval: 30000,
    staleTime: 15000,
  });

  const notifications = data?.data?.notifications || data?.notifications || [];
  const unreadCount = data?.data?.unreadCount || data?.unreadCount || notifications.filter((n: any) => !n.isRead).length;

  const getIcon = (type: string) => {
    switch (type) {
      case "DocumentViewed": return <Eye className="h-4 w-4 text-blue-500" />;
      case "DocumentSigned": return <Pen className="h-4 w-4 text-green-500" />;
      case "DocumentDeclined": return <XCircle className="h-4 w-4 text-red-500" />;
      case "DocumentCompleted": return <CheckCheck className="h-4 w-4 text-emerald-500" />;
      case "SignatureExpired": return <Clock className="h-4 w-4 text-orange-500" />;
      default: return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const markAsRead = async (id: string) => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");
    await fetch(getApiUrl(`/api/notifications/${id}/read`), {
      method: "PATCH",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    queryClient.invalidateQueries({ queryKey: ["/api/notifications/recent"] });
    queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="button-notifications-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>{language === "pt" ? "Notificações" : "Notifications"}</span>
          {unreadCount > 0 && <Badge variant="secondary" className="text-xs">{unreadCount} {language === "pt" ? "novas" : "new"}</Badge>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {language === "pt" ? "Nenhuma notificação" : "No notifications"}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            {notifications.slice(0, 5).map((n: any) => (
              <DropdownMenuItem
                key={n.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${!n.isRead ? "bg-primary/5" : ""}`}
                onClick={() => { markAsRead(n.id); setOpen(false); window.location.href = n.documentId ? `/documents/${n.documentId}` : "/notifications"; }}
              >
                <div className="mt-0.5 shrink-0">{getIcon(n.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm line-clamp-2">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(() => { try { return formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: language === "pt" ? ptBR : enUS }); } catch { return ""; } })()}
                  </p>
                </div>
                {!n.isRead && <div className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
              </DropdownMenuItem>
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/notifications" className="w-full text-center text-sm text-primary font-medium justify-center" onClick={() => setOpen(false)}>
            {language === "pt" ? "Ver todas as notificações" : "View all notifications"}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type MenuGroupId = "work" | "records" | "admin" | "system";

type MenuItem = { title: string; url: string; icon: any; group: MenuGroupId; adminOnly?: boolean; superAdminOnly?: boolean };

// Sitemap-style groups (like the Dynamics 365 area menu). Order here is the order on screen.
function getMenuGroups(t: (key: string) => string): Array<{ id: MenuGroupId; label: string }> {
  return [
    { id: "work", label: t("nav.groupWork") },
    { id: "records", label: t("nav.groupRecords") },
    { id: "admin", label: t("nav.groupAdmin") },
    { id: "system", label: t("nav.groupSystem") },
  ];
}

// Clientes and Leads show only when the company has the CRM module on (company admins, in Settings).
function getMenuItems(t: (key: string) => string, crmEnabled = false): MenuItem[] {
  return ([
    { title: t("nav.dashboard"), url: "/dashboard", icon: LayoutDashboard, group: "work" },
    { title: t("nav.documents"), url: "/documents", icon: FileText, group: "work" },
    { title: t("nav.library"), url: "/modelos", icon: BookOpen, group: "work" },
    { title: t("nav.signatures"), url: "/signatures", icon: Send, group: "work" },
    { title: (t("nav.documents") === "Documentos" ? "Clientes" : "Contacts"), url: "/contacts", icon: Users, group: "records" },
    { title: "Leads", url: "/leads", icon: Target, group: "records" },
    { title: t("nav.templates"), url: "/document-templates", icon: FileStack, group: "records", adminOnly: true },
    // Notifications moved to bell icon in header
    { title: t("nav.auditLogs"), url: "/audit-logs", icon: History, group: "admin", adminOnly: true },
    { title: "Admin", url: "/admin", icon: ShieldCheck, group: "admin", superAdminOnly: true },
    { title: t("nav.settings"), url: "/settings", icon: Settings, group: "system" },
    { title: t("nav.helpSupport"), url: "/help", icon: HelpCircle, group: "system" },
  ] as MenuItem[]).filter((item) => crmEnabled || (item.url !== "/contacts" && item.url !== "/leads"));
}

export function AppLayout({ children, user, onLogout, plan, currentPage }: AppLayoutProps) {
  const { sidebarPlacement } = useSettings();
  const [leaving, setLeaving] = useState(false);

  // "Sair": a short goodbye from the mascot, then the page's own logout (token cleanup + redirect).
  const logout = () => {
    if (leaving) return;
    setLeaving(true);
    window.setTimeout(onLogout, 1300);
  };

  const layout = sidebarPlacement === "left"
    ? <LeftSidebarLayout user={user} onLogout={logout} plan={plan} currentPage={currentPage}>{children}</LeftSidebarLayout>
    : <TopNavLayout user={user} onLogout={logout} plan={plan} currentPage={currentPage}>{children}</TopNavLayout>;
  return (
    <>
      {layout}
      {leaving && <LogoutFarewell firstName={user?.firstName} />}
    </>
  );
}

/** Full-screen goodbye: the mascot waves, says bye and dozes off while the app fades. */
function LogoutFarewell({ firstName }: { firstName?: string }) {
  const { language } = useLanguage();
  const [mood, setMood] = useState<"wave" | "sleepy">("wave");
  useEffect(() => {
    const t = window.setTimeout(() => setMood("sleepy"), 850);
    return () => window.clearTimeout(t);
  }, []);
  const name = firstName?.split(" ")[0];
  const text = language === "pt"
    ? (mood === "wave" ? `Até logo${name ? `, ${name}` : ""}! 👋` : "Saindo da conta…")
    : (mood === "wave" ? `See you${name ? `, ${name}` : ""}! 👋` : "Signing out…");
  return (
    <div className="fs-logout-farewell" data-testid="logout-farewell" aria-live="polite">
      <Mascot mood={mood} size={128} />
      <p className="fs-logout-farewell-text">{text}</p>
    </div>
  );
}

function getUserInitials(user: any): string {
  if (user?.firstName && user?.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  if (user?.firstName) {
    return user.firstName.substring(0, 2).toUpperCase();
  }
  if (user?.email) {
    return user.email.substring(0, 2).toUpperCase();
  }
  return "U";
}

function TopNavLayout({ children, user, onLogout, plan, currentPage }: AppLayoutProps) {
  const { t } = useLanguage();
  const crmEnabled = useCrmMenuFlag(user?.id);
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const activePage = currentPage || location;

  const { data: companyData } = useQuery<any>({
    queryKey: ["/api/companies/my"],
    enabled: !!user,
  });
  const resolvedPlan = resolvePlan(companyData, plan, user);

  const isAdminOrOwner = companyData?.members?.some(
    (m: any) => m.userId === user?.id && (m.role === "owner" || m.role === "admin")
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-background">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 py-3 flex items-center justify-between gap-4">
          <Link href="/dashboard">
            <BrandLogo size="lg" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {getMenuGroups(t).map((g) => {
              const items = getMenuItems(t, crmEnabled)
                .filter(item => item.url !== "/settings" && item.url !== "/help")
                .filter(item => (!item.adminOnly || isAdminOrOwner || user?.isSuperAdmin) && (!item.superAdminOnly || user?.isSuperAdmin))
                .filter(item => item.group === g.id);
              if (items.length === 0) return null;
              const groupActive = items.some(i => i.url === activePage);
              return (
                <DropdownMenu key={g.id}>
                  <DropdownMenuTrigger asChild>
                    <Button variant={groupActive ? "secondary" : "ghost"} className="text-sm gap-1" data-testid={`nav-group-${g.id}`} data-guide={`nav-group-${g.id}`}>
                      {g.label}
                      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {items.map((item) => (
                      <DropdownMenuItem key={item.url} asChild>
                        <Link href={item.url} className={`flex items-center gap-2 cursor-pointer ${activePage === item.url ? "bg-accent" : ""}`} data-testid={`nav-${item.url.replace("/", "")}`}>
                          <item.icon className="h-4 w-4" />
                          {item.title}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            {resolvedPlan && <Badge variant="secondary" className="hidden sm:flex" data-testid="badge-plan">{planLabel(resolvedPlan, t)}</Badge>}
            <NotificationBell />
            <LanguageToggle />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full hover-elevate active-elevate-2 p-0.5 cursor-pointer" data-testid="button-profile-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profileImageUrl} alt={user?.firstName || t("ui.userFallback")} />
                    <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-profile">
                  <User className="mr-2 h-4 w-4" />
                  {t("profile")}
                </DropdownMenuItem>
                {isAdminOrOwner && (
                  <DropdownMenuItem onClick={() => navigate("/settings?tab=team")} data-testid="menu-manage-users">
                    <Users className="mr-2 h-4 w-4" />
                    {t("manageUsers")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-3 sm:px-6 py-4 sm:py-8 overflow-x-hidden fs-page-enter">
        <BillingNotice />
        {children}
      </main>
    </div>
  );
}

/** Phone drawer: close it after a tap on a menu item (the sheet does not close by itself when the route changes). */
function CloseMobileSidebarOnNavigate() {
  const { isMobile, setOpenMobile } = useSidebar();
  const [location] = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (isMobile) setOpenMobile(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  return null;
}

function LeftSidebarLayout({ children, user, onLogout, plan, currentPage }: AppLayoutProps) {
  const { t } = useLanguage();
  const crmEnabled = useCrmMenuFlag(user?.id);
  const [location] = useLocation();
  const [, navigate] = useLocation();
  const activePage = currentPage || location;

  const { data: companyData } = useQuery<any>({
    queryKey: ["/api/companies/my"],
    enabled: !!user,
  });
  const resolvedPlan = resolvePlan(companyData, plan, user);

  const isAdminOrOwner = companyData?.members?.some(
    (m: any) => m.userId === user?.id && (m.role === "owner" || m.role === "admin")
  );

  const menuItems = getMenuItems(t, crmEnabled).filter(
    item => (!item.adminOnly || isAdminOrOwner || user?.isSuperAdmin) && (!item.superAdminOnly || user?.isSuperAdmin)
  );

  const style = {
    "--sidebar-width": "13rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <SidebarProvider style={style as React.CSSProperties}>
      <CloseMobileSidebarOnNavigate />
      <div className="flex h-screen w-full">
        <Sidebar>
          <SidebarHeader className="p-3 space-y-3">
            <Link href="/dashboard">
              <BrandLogo size="lg" />
            </Link>
            <CompanySwitcher user={user} />
          </SidebarHeader>
          <SidebarContent className="px-2">
            {getMenuGroups(t).map((g) => {
              const items = menuItems.filter((i) => i.group === g.id);
              if (items.length === 0) return null;
              return (
                <SidebarGroup key={g.id} className="py-1">
                  <SidebarGroupLabel>{g.label}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild isActive={activePage === item.url}>
                            <Link href={item.url} data-guide={`nav-${item.url.replace("/", "")}`}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
          </SidebarContent>
          <SidebarFooter className="p-3 space-y-2">
            <div className="flex items-center gap-2 min-h-5">
              {resolvedPlan && <Badge variant="secondary" className="text-xs" data-testid="badge-plan">{planLabel(resolvedPlan, t)}</Badge>}
            </div>
            <div className="flex items-center gap-1">
              <LanguageToggle />
              <ThemeToggle />
              <Button variant="ghost" size="icon" onClick={onLogout} data-testid="button-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </SidebarFooter>
        </Sidebar>
        <div className="flex flex-col flex-1 overflow-hidden">
          <header className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 py-2 border-b bg-background">
            {/* On a phone the sidebar is a drawer: a labelled, finger-sized "Menu" button instead of the small panel icon. */}
            <SidebarTrigger
              data-testid="button-sidebar-toggle"
              aria-label={t("nav.menu")}
              className="h-10 w-auto gap-2 px-3 border border-border text-sm font-medium md:h-7 md:w-7 md:gap-0 md:px-0 md:border-0 md:text-xs [&_svg]:size-5 md:[&_svg]:size-4"
            >
              <Menu className="md:hidden" />
              <PanelLeft className="hidden md:block" />
              <span className="md:hidden">{t("nav.menu")}</span>
            </SidebarTrigger>
            <div className="flex-1" />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full hover-elevate active-elevate-2 p-1 cursor-pointer" data-testid="button-profile-menu">
                  <span className="text-sm text-muted-foreground hidden sm:block">
                    {user?.firstName || user?.email}
                  </span>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profileImageUrl} alt={user?.firstName || t("ui.userFallback")} />
                    <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-profile">
                  <User className="mr-2 h-4 w-4" />
                  {t("profile")}
                </DropdownMenuItem>
                {isAdminOrOwner && (
                  <DropdownMenuItem onClick={() => navigate("/settings?tab=team")} data-testid="menu-manage-users">
                    <Users className="mr-2 h-4 w-4" />
                    {t("manageUsers")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="flex-1 overflow-auto overflow-x-hidden p-3 sm:p-4 fs-page-enter">
            <div className="mx-auto max-w-6xl">
              <BillingNotice />
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
